import type { TransferStored, JudoRestApi } from "@judo/actions";
import { useMuiProOptional } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { Column, Link, RelationType } from "@judo/model-api";
import { getLinkTestId } from "@judo/test-ids";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowSelectionModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type OptionRecord = Record<string, unknown>;

export interface LinkSelectorDialogProps {
	open: boolean;
	element: Link;
	relationType: RelationType;
	api: JudoRestApi;
	transfer?: OptionRecord;
	onConfirm: (selected: OptionRecord) => void;
	onCancel: () => void;
}

/**
 * A selector dialog for Link components.
 *
 * Shows a DataGrid table with the available relation range,
 * supports text filtering, pagination, and single-row selection.
 */
export function LinkSelectorDialog({
	open,
	element,
	relationType,
	api,
	transfer,
	onConfirm,
	onCancel,
}: LinkSelectorDialogProps) {
	const testId = `${getLinkTestId(element)}::selector`;
	const { label } = useModelLabel(element);

	// MUI Pro detection — use DataGridPro when available for advanced features
	const muiPro = useMuiProOptional();
	const DataGridComponent = muiPro?.getComponent("DataGrid") ?? DataGrid;

	// State
	const [rows, setRows] = useState<OptionRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [filterText, setFilterText] = useState("");
	const [selectedRow, setSelectedRow] = useState<OptionRecord | null>(null);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: element.selectorRowsPerPage ?? 10,
	});

	// Guard against multiple initial loads
	const _initialLoadDone = useRef(false);

	// Determine the first string attribute name on the target type for filtering
	const filterAttributeName = useMemo(() => {
		if (!relationType?.target?.attributes) return undefined;
		const stringAttr = relationType.target.attributes.find((attr) => attr.dataType?.["@type"] === "StringType");
		return stringAttr?.name;
	}, [relationType]);

	// Build columns from element.parts
	const columns: GridColDef[] = useMemo(() => {
		return element.parts.map((col: Column) => ({
			field: col.attributeType?.name ?? col.name,
			headerName: col.label ?? col.name,
			renderHeader: () => <TranslatedColumnHeader column={col} />,
			width: col.width ? Number.parseInt(col.width, 10) : 150,
			sortable: true,
			filterable: false,
		}));
	}, [element.parts]);

	// Fetch data from backend
	const fetchData = useCallback(
		async (pageNum: number, pageSize: number, filter: string) => {
			setLoading(true);
			try {
				const queryCustomizer: Record<string, unknown> = {
					_seek: { limit: pageSize, offset: pageNum * pageSize },
				};

				// Apply text filter on the first string attribute
				if (filter && filterAttributeName) {
					queryCustomizer[filterAttributeName] = [{ operator: "like", value: `%${filter}%` }];
				}

				const response = await api.getRelationRange(
					relationType,
					transfer as TransferStored | undefined,
					queryCustomizer as any
				);

				setRows(response.data as OptionRecord[]);
				// If response includes count, use it
				if (typeof (response as any).totalCount === "number") {
					setRowCount((response as any).totalCount);
				} else {
					// Estimate: if we got a full page, there might be more
					setRowCount(
						response.data.length < pageSize ? pageNum * pageSize + response.data.length : (pageNum + 1) * pageSize + 1
					);
				}
			} catch (error) {
				console.error("Failed to fetch selector data:", error);
				setRows([]);
				setRowCount(0);
			} finally {
				setLoading(false);
			}
		},
		[api, relationType, transfer, filterAttributeName]
	);

	// Initial load and on pagination/filter change
	useEffect(() => {
		if (!open) return;
		fetchData(paginationModel.page, paginationModel.pageSize, filterText);
	}, [open, paginationModel.page, paginationModel.pageSize, filterText, fetchData]);

	// Handle filter input with debounce
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const handleFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}
		debounceRef.current = setTimeout(() => {
			setFilterText(value);
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
		}, 300);
	}, []);

	// Handle row selection
	const handleRowSelectionChange = useCallback(
		(model: GridRowSelectionModel) => {
			const selectedIds = model.type === "include" ? Array.from(model.ids) : [];
			if (selectedIds.length > 0) {
				const selected = rows.find(
					(row) => (row.__signedIdentifier ?? row.__identifier ?? row.__tempId ?? (row as any).id) === selectedIds[0]
				);
				setSelectedRow(selected ?? null);
			} else {
				setSelectedRow(null);
			}
		},
		[rows]
	);

	// Handle confirm
	const handleConfirm = useCallback(() => {
		if (selectedRow) {
			onConfirm(selectedRow);
		}
	}, [selectedRow, onConfirm]);

	// Page size options
	const pageSizeOptions = useMemo(() => {
		const defaultOptions = [5, 10, 25, 50];
		const modeledPageSize = element.selectorRowsPerPage ?? 10;
		if (!defaultOptions.includes(modeledPageSize)) {
			return [...defaultOptions, modeledPageSize].toSorted((a, b) => a - b);
		}
		return defaultOptions;
	}, [element.selectorRowsPerPage]);

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth data-testid={testId}>
			<DialogTitle data-testid={`${testId}::title`}>{label}</DialogTitle>
			<DialogContent>
				<Box sx={{ mb: 2, mt: 1 }}>
					<TextField
						fullWidth
						size="small"
						placeholder={`Filter by ${filterAttributeName ?? "name"}...`}
						onChange={handleFilterChange}
						data-testid={`${testId}::filter`}
						slotProps={{
							inputLabel: { shrink: true },
						}}
					/>
				</Box>
				<Box sx={{ height: 400 }}>
					<DataGridComponent
						columns={columns}
						rows={rows}
						rowCount={rowCount}
						loading={loading}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						paginationMode="server"
						pageSizeOptions={pageSizeOptions}
						getRowId={(row: any) => row.__signedIdentifier ?? row.__identifier ?? row.__tempId ?? row.id}
						isRowSelectable={(params: any) => params.row?.__selected !== true}
						onRowSelectionModelChange={handleRowSelectionChange}
						data-testid={`${testId}::grid`}
						sx={{ height: "100%" }}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onCancel} data-testid={`${testId}::cancel`}>
					Cancel
				</Button>
				<Button onClick={handleConfirm} variant="contained" disabled={!selectedRow} data-testid={`${testId}::confirm`}>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
}

/** Translated column header component for selector DataGrid. */
function TranslatedColumnHeader({ column }: { column: Column }) {
	const { label } = useModelLabel(column);
	return <>{label}</>;
}
